import React from 'react';
import { Chip, FormControlLabel, styled } from '@mui/material';
import { dateString } from 'shared/utils';
import { useRequest } from 'shared/hooks';
import StyledSwitch from 'shared/components/styled/switch';

const WarnChip = styled(Chip)(({ theme }) => ({
  background: (theme.vars || theme).palette.archivedFill,
  color: (theme.vars || theme).palette.archivedInk,
  fontWeight: 'bold',
}));

/**
 * The archived marker for a record header (TenantNav / BuildingNav), not a tab route: the
 * amber outline is colour alone, so the text must show on every tab (WCAG 1.4.1).
 *
 * @param {Object} props
 * @param {string} [props.archivedAt='']
 */
export function ArchivedBadge(props) {
  const { archivedAt = '' } = props;

  if (!archivedAt) return null;
  if (new Date(archivedAt).valueOf()) {
    return <WarnChip label={`Archived on ${dateString(archivedAt)}`} />;
  }
  return <WarnChip label="Archived" />;
}

// The same marker, sized for a table row. Rendered by StickyHeadTable.
export function ArchivedRowChip() {
  return <WarnChip label="Archived" size="small" sx={{ mt: 0.5 }} />;
}

/**
 * @param {Object} props
 * @param {Function} props.afterToggle
 * @param {Object<string, *>} props.requestBody
 * @param {boolean} props.showToggle
 * @param {string} [props.archivedAt='']
 * @param {boolean} [props.disabled=false]
 */
export default function ArchiveSwitch(props) {
  const {
    afterToggle,
    archivedAt,
    disabled,
    requestBody,
    showToggle,
  } = props;

  const { request } = useRequest();

  const toggleArchived = async () => {
    const result = await request(requestBody);
    if (result) afterToggle();
  };

  if (!showToggle) return null;

  return (
    <FormControlLabel
      control={(
        <StyledSwitch
          checked={!!archivedAt}
          disabled={disabled}
          onChange={toggleArchived}
          type="checkbox"
        />
      )}
      label="Archive"
    />
  );
}
